import { useCallback, useEffect, useRef, useState } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Text from "../../../shared/ui/Typography/Text";

import ClientContactRow from "../ui/ClientContactRow";
import {
  fetchClientContacts,
  createClientContact,
  updateClientContact,
  deleteClientContact,
} from "../api/clientContacts";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientContactsEditor — feature widget. Owns the load lifecycle and
// the rows array. No Redux slice — see
// migration/phase12-nested-array-plan.md §11.
//
// Each row's full state lives in the parent's `rows` array. The row
// component is presentational (see ui/ClientContactRow.js). Parent
// passes change callbacks; row dispatches updates via them.

const EDITABLE_FIELDS = ["type", "value", "is_primary"];

const DEFAULT_TYPE = "phone";

const toBaseline = (server) => ({
  type: server.type,
  value: server.value,
  is_primary: Boolean(server.is_primary),
});

const toRow = (server) => ({
  key: `srv-${server.id}`,
  id: server.id,
  type: server.type,
  value: server.value ?? "",
  is_primary: Boolean(server.is_primary),
  baseline: toBaseline(server),
  saveState: "idle",
  deleteState: "idle",
  fieldErrors: {},
});

const buildDraft = (tempId) => ({
  key: `drf-${tempId}`,
  id: null,
  type: DEFAULT_TYPE,
  value: "",
  is_primary: false,
  baseline: null,
  saveState: "idle",
  deleteState: "idle",
  fieldErrors: {},
});

const computeDirty = (row) => {
  if (row.id == null) return true; // drafts are always dirty
  const b = row.baseline;
  if (!b) return true;
  return (
    row.type !== b.type
    || row.value !== b.value
    || Boolean(row.is_primary) !== Boolean(b.is_primary)
  );
};

const buildPatchBody = (row) => {
  const b = row.baseline || {};
  const diff = {};
  if (row.type !== b.type) diff.type = row.type;
  if (row.value !== b.value) diff.value = row.value;
  if (Boolean(row.is_primary) !== Boolean(b.is_primary)) {
    diff.is_primary = Boolean(row.is_primary);
  }
  return diff;
};

const buildCreateBody = (row) => ({
  type: row.type,
  value: row.value,
  is_primary: Boolean(row.is_primary),
});

const omitKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

function ClientContactsEditor({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState(null);

  const draftCounterRef = useRef(0);

  // Keep a ref to rows so handleRowDelete can read the latest snapshot
  // without depending on rows in its useCallback dependency list.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // ============================================================
  // LOAD LIFECYCLE
  // ============================================================
  useEffect(() => {
    if (clientId == null) {
      setLoadState("not-found");
      return undefined;
    }

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchClientContacts(clientId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRows(list.map(toRow));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 403) {
          setLoadState("forbidden");
        } else if (status === 404) {
          setLoadState("not-found");
        } else {
          setLoadError(messageFromError(err));
          setLoadState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // ============================================================
  // COLLECTION HANDLERS
  // ============================================================

  const handleAddRow = useCallback(() => {
    draftCounterRef.current += 1;
    const draft = buildDraft(draftCounterRef.current);
    setRows((prev) => [...prev, draft]);
  }, []);

  const handleRowChange = useCallback((rowKey, field, nextValue) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          [field]: nextValue,
          fieldErrors: omitKey(omitKey(row.fieldErrors, field), "_form"),
          saveState: row.saveState === "saved" ? "idle" : row.saveState,
        };
      }),
    );
  }, []);

  const handleRowCancel = useCallback((rowKey) => {
    setRows((prev) => {
      const target = prev.find((row) => row.key === rowKey);
      if (!target) return prev;
      // Draft → discard outright.
      if (target.id == null) {
        return prev.filter((row) => row.key !== rowKey);
      }
      // Existing → revert to baseline.
      const baseline = target.baseline || {};
      return prev.map((row) => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          type: baseline.type,
          value: baseline.value,
          is_primary: Boolean(baseline.is_primary),
          fieldErrors: {},
          saveState: "idle",
        };
      });
    });
  }, []);

  // ============================================================
  // ROW SAVE LIFECYCLE — idle → saving → saved | error
  // ============================================================
  const handleRowSave = useCallback(
    async (rowKey) => {
      // Snapshot the row at click-time from rowsRef.current — the same
      // ref handleRowDelete reads. Reading the snapshot via a side-effect
      // inside the setRows updater is unreliable: React only invokes the
      // updater synchronously when the eager-bail-out optimization fires;
      // under concurrent rendering, transitions, or batched updates, the
      // updater is deferred and the next line runs before snapshot is
      // assigned, silently aborting the save (the reported create-failure).
      const snapshot = rowsRef.current.find((row) => row.key === rowKey);
      if (!snapshot) return;
      if (snapshot.saveState === "saving") return; // double-click guard

      setRows((prev) =>
        prev.map((row) =>
          row.key === rowKey
            ? { ...row, saveState: "saving", fieldErrors: {} }
            : row,
        ),
      );

      const isDraft = snapshot.id == null;

      try {
        const serverRow = isDraft
          ? await createClientContact(clientId, buildCreateBody(snapshot))
          : await updateClientContact(
              clientId,
              snapshot.id,
              buildPatchBody(snapshot),
            );

        setRows((prev) =>
          prev.map((row) => {
            if (row.key !== rowKey) return row;
            // Stable key — never recomputed.
            return {
              ...row,
              id: serverRow.id,
              type: serverRow.type,
              value: serverRow.value ?? "",
              is_primary: Boolean(serverRow.is_primary),
              baseline: toBaseline(serverRow),
              saveState: "saved",
              fieldErrors: {},
            };
          }),
        );
      } catch (err) {
        const parsed = parseDrfFieldErrors(err, EDITABLE_FIELDS);
        setRows((prev) =>
          prev.map((row) => {
            if (row.key !== rowKey) return row;
            return {
              ...row,
              saveState: "error",
              fieldErrors: parsed,
            };
          }),
        );
      }
    },
    [clientId],
  );

  // ============================================================
  // ROW DELETE LIFECYCLE
  // ============================================================
  const handleRowDeleteRequest = useCallback((rowKey) => {
    const target = rowsRef.current.find((row) => row.key === rowKey);
    if (!target) return;

    // Draft: discard immediately, no confirm.
    if (target.id == null) {
      setRows((prev) => prev.filter((row) => row.key !== rowKey));
      setDeleteConfirmKey(null);
      return;
    }

    setDeleteConfirmKey(rowKey);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmKey(null);
  }, []);

  const handleConfirmDelete = useCallback(
    async () => {
      const rowKey = deleteConfirmKey;
      if (!rowKey) return;
      const target = rowsRef.current.find((row) => row.key === rowKey);
      if (!target || target.id == null) {
        setDeleteConfirmKey(null);
        return;
      }
      if (target.deleteState === "deleting") return;

      setDeleteConfirmKey(null);
      setRows((prev) =>
        prev.map((row) => {
          if (row.key !== rowKey) return row;
          return {
            ...row,
            deleteState: "deleting",
            fieldErrors: omitKey(row.fieldErrors, "_form"),
          };
        }),
      );

      try {
        await deleteClientContact(clientId, target.id);
        setRows((prev) => prev.filter((row) => row.key !== rowKey));
      } catch (err) {
        setRows((prev) =>
          prev.map((row) => {
            if (row.key !== rowKey) return row;
            return {
              ...row,
              deleteState: "error",
              fieldErrors: {
                ...row.fieldErrors,
                _form: messageFromError(err),
              },
            };
          }),
        );
      }
    },
    [clientId, deleteConfirmKey],
  );

  // ============================================================
  // RENDER — gated by loadState
  // ============================================================
  if (loadState === "loading") {
    return (
      <div className="feature-client-contacts__state">
        <Spinner size="lg" label="Завантаження каналів зв'язку" />
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="feature-client-contacts__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description="Редагувати канали зв'язку може лише власник клієнта або реалтор зі спільним доступом на редагування."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <div className="feature-client-contacts__state">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Клієнта не знайдено"
          description="Перевірте адресу сторінки або поверніться до списку клієнтів."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-client-contacts__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити канали"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
        />
      </div>
    );
  }

  const deleteTarget = deleteConfirmKey
    ? rows.find((row) => row.key === deleteConfirmKey)
    : null;
  const deleteBusy = Boolean(
    deleteTarget && deleteTarget.deleteState === "deleting",
  );

  return (
    <div
      className="feature-client-contacts__editor"
      data-feature="client-contacts-editor"
    >
      {rows.length === 0 ? (
        <div className="feature-client-contacts__empty">
          <EmptyState
            icon="fa-solid fa-address-book"
            title="Каналів ще немає"
            description="Додайте перший канал зв'язку — Telegram, додатковий номер тощо."
            size="md"
          />
        </div>
      ) : (
        <ul className="feature-client-contacts__list">
          {rows.map((row) => (
            <ClientContactRow
              key={row.key}
              row={row}
              dirty={computeDirty(row)}
              onChange={handleRowChange}
              onSave={handleRowSave}
              onCancel={handleRowCancel}
              onDelete={handleRowDeleteRequest}
            />
          ))}
        </ul>
      )}

      <div className="feature-client-contacts__collection-actions">
        <button
          type="button"
          className="feature-client-contacts__add-button"
          onClick={handleAddRow}
        >
          <span aria-hidden="true">+</span>
          <span>Додати канал</span>
        </button>
        {rows.length > 0 ? (
          <Text
            as="span"
            size="xs"
            tone="secondary"
            className="feature-client-contacts__hint"
          >
            Кожен рядок зберігається окремо.
          </Text>
        ) : null}
      </div>

      {deleteTarget ? (
        <div
          className="feature-client-contacts__delete-confirm"
          role="alertdialog"
          aria-labelledby="client-contact-delete-title"
          aria-describedby="client-contact-delete-desc"
          data-testid="client-contact-delete-confirm"
        >
          <p id="client-contact-delete-title" className="feature-client-contacts__delete-title">
            Видалити канал?
          </p>
          <p id="client-contact-delete-desc" className="feature-client-contacts__delete-desc">
            Видалити цей канал? Цю дію не можна скасувати.
          </p>
          <div className="feature-client-contacts__delete-actions">
            <button
              type="button"
              className="feature-client-contacts__cancel-button"
              onClick={handleCancelDelete}
              disabled={deleteBusy}
            >
              Скасувати
            </button>
            <button
              type="button"
              className="feature-client-contacts__delete-button"
              onClick={() => void handleConfirmDelete()}
              disabled={deleteBusy}
              data-testid="client-contact-delete-confirm-action"
            >
              {deleteBusy ? (
                <Spinner size="sm" label="Видалення" />
              ) : (
                "Видалити"
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ClientContactsEditor;
